import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const Settings: React.FC = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isElectron, setIsElectron] = useState(false);

  useEffect(() => {
    const electronAPI = (window as any).electronAPI;
    setIsElectron(!!electronAPI);

    if (electronAPI) {
      loadConfig();
    } else {
      setLoading(false);
    }
  }, []);

  const loadConfig = async () => {
    try {
      const electronAPI = (window as any).electronAPI;
      const loadedConfig = await electronAPI.getConfig();
      setConfig(loadedConfig);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const electronAPI = (window as any).electronAPI;
      await electronAPI.updateConfig(config);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) {
      return;
    }

    try {
      setSaving(true);
      const electronAPI = (window as any).electronAPI;
      await electronAPI.resetConfig();
      await loadConfig();
      toast.success('Settings reset to defaults');
    } catch (error) {
      console.error('Failed to reset config:', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key: string, value: any) => {
    setConfig({
      ...config,
      [key]: value
    });
  };

  if (!isElectron) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Settings are only available in the desktop application.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Application Settings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure your trading system preferences
          </p>
        </div>

        {/* Settings Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Application Settings */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Application</h2>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config?.autoUpdate || false}
                  onChange={(e) => handleChange('autoUpdate', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Automatically check for updates
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config?.startMinimized || false}
                  onChange={(e) => handleChange('startMinimized', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Start application minimized
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config?.minimizeToTray || false}
                  onChange={(e) => handleChange('minimizeToTray', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Minimize to system tray
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config?.launchOnStartup || false}
                  onChange={(e) => handleChange('launchOnStartup', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Launch on system startup
                </span>
              </label>
            </div>
          </div>

          {/* Backend Settings */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Backend Server</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port
                </label>
                <input
                  type="number"
                  value={config?.backendPort || 8000}
                  onChange={(e) => handleChange('backendPort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Log Level
                </label>
                <select
                  value={config?.logLevel || 'INFO'}
                  onChange={(e) => handleChange('logLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="DEBUG">Debug</option>
                  <option value="INFO">Info</option>
                  <option value="WARNING">Warning</option>
                  <option value="ERROR">Error</option>
                </select>
              </div>
            </div>
          </div>

          {/* Database Settings */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Database</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Database Name
                </label>
                <input
                  type="text"
                  value={config?.databaseName || 'trading_db'}
                  onChange={(e) => handleChange('databaseName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Database Port
                </label>
                <input
                  type="number"
                  value={config?.databasePort || 5432}
                  onChange={(e) => handleChange('databasePort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Kite Connect Settings */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Kite Connect</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redirect URL
              </label>
              <input
                type="text"
                value={config?.kiteRedirectUrl || ''}
                onChange={(e) => handleChange('kiteRedirectUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="http://localhost:8000/api/v1/accounts/kite/callback"
              />
              <p className="mt-1 text-xs text-gray-500">
                Must match the redirect URL configured in your Kite Connect app
              </p>
            </div>
          </div>

          {/* Theme */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Theme
              </label>
              <select
                value={config?.theme || 'light'}
                onChange={(e) => handleChange('theme', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
          </div>

          {/* Version Info & Updates */}
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Application Version</p>
                <p className="text-sm text-gray-600">
                  <span className="font-mono">{config?.version || 'Unknown'}</span>
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const electronAPI = (window as any).electronAPI;
                    if (electronAPI) {
                      toast.loading('Checking for updates...');
                      await electronAPI.checkForUpdates(true);
                    }
                  } catch (error) {
                    console.error('Failed to check for updates:', error);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Check for Updates
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset to Defaults
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
